import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Calendar,
  Video,
  Mail,
  Clock,
  Target,
  Sparkles,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

interface BookingSuccessProps {
  bookingData?: {
    eventId?: string;
    htmlLink?: string;
    date?: string;
    time?: string;
  };
  onBookAnother: () => void;
}

const nextSteps = [
  {
    number: 1,
    icon: Mail,
    title: 'Confirmation Email',
    description: "You'll receive a calendar invitation with all meeting details",
  },
  {
    number: 2,
    icon: Video,
    title: 'Discovery Call',
    description: "We'll discuss your goals and explore opportunities together",
  },
  {
    number: 3,
    icon: Target,
    title: 'Next Steps',
    description: "Based on your interests, we'll present relevant investment opportunities",
  },
];

const callFeatures = [
  { icon: Clock, label: '30 Minutes', description: 'Focused discussion' },
  { icon: CheckCircle, label: 'No Obligation', description: 'Completely complimentary' },
  { icon: Sparkles, label: 'Personalized', description: 'Tailored to your goals' },
];

export const BookingSuccess: React.FC<BookingSuccessProps> = ({
  bookingData,
  onBookAnother,
}) => {
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-4xl w-full"
      >
        <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-luxury-gold/20 p-6 sm:p-8 md:p-12 shadow-xl relative overflow-hidden">
          {/* Decorative corner accents */}
          <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-luxury-gold/5 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-luxury-gold/5 to-transparent rounded-tr-full" />
          {/* Success Icon */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6"
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-luxury-gold via-luxury-gold-light to-luxury-gold opacity-20 animate-pulse" />
              <div className="absolute inset-[3px] bg-white dark:bg-white/5 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-500" />
              </div>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-4"
            >
              <Sparkles className="w-4 h-4 text-luxury-gold mr-2" />
              <span className="text-sm font-medium text-luxury-gold">Consultation Confirmed</span>
              <Calendar className="w-4 h-4 text-luxury-gold ml-2" />
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-luxury-text dark:text-luxury-text-dark mb-4"
            >
              Your Investment Journey{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-gold-light">
                Begins Now
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-luxury-text-muted dark:text-luxury-text-muted-dark max-w-xl mx-auto"
            >
              {bookingData?.htmlLink
                ? 'Your discovery call is confirmed. We look forward to discussing your investment goals and exploring opportunities together.'
                : "Confirmation sent! We'll be in touch shortly with meeting details and next steps."}
            </motion.p>
          </div>

          {/* Google Meet Link Button */}
          {bookingData?.htmlLink && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center mb-10"
            >
              <a
                href={bookingData.htmlLink}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-luxury-gold to-luxury-gold-light text-luxury-text font-semibold rounded-full shadow-gold-lg hover:shadow-gold-xl hover:-translate-y-1 transition-all duration-300"
              >
                <Video className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" />
                Access Your Meeting Link
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </motion.div>
          )}

          {/* What Happens Next Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white/60 dark:bg-white/5 rounded-2xl p-6 sm:p-8 mb-8 border border-luxury-gold/20 shadow-md relative overflow-hidden"
          >
            {/* Decorative line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-luxury-gold/10 border border-luxury-gold/20 mb-3">
                <ArrowRight className="w-6 h-6 text-luxury-gold" />
              </div>
              <h2 className="text-xl font-semibold text-luxury-text dark:text-luxury-text-dark">
                What Happens <span className="text-luxury-gold">Next</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {nextSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.number}
                    className="relative text-center p-4 sm:p-5 rounded-xl bg-white dark:bg-white/5 border border-luxury-gold/20"
                  >
                    <div className="relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-luxury-gold/10 border border-luxury-gold/20 mb-3">
                      <Icon className="w-6 h-6 text-luxury-gold" />
                      <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-luxury-gold to-luxury-gold-light flex items-center justify-center text-luxury-text text-xs font-bold">
                        {step.number}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-luxury-text dark:text-luxury-text-dark mb-1">
                      {step.title}
                    </h3>
                    <p className="text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Call Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="grid grid-cols-3 gap-4 mb-8"
          >
            {callFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.label}
                  className="text-center p-4 rounded-xl bg-white/60 dark:bg-white/5 border border-luxury-gold/20 shadow-md"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-luxury-gold/10 mb-2">
                    <Icon className="w-5 h-5 text-luxury-gold" />
                  </div>
                  <div className="text-sm font-semibold text-luxury-gold mb-0.5">
                    {feature.label}
                  </div>
                  <p className="text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={onBookAnother}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-luxury-gold to-luxury-gold-light text-luxury-text font-medium rounded-full shadow-gold-lg hover:shadow-gold-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Another Call
            </button>

            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 text-luxury-gold font-medium rounded-full border border-luxury-gold/30 hover:bg-luxury-gold/5 transition-all duration-300"
            >
              Return to Home
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default BookingSuccess;
